import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { BotIcon } from 'lucide-react';

import { useVirtualizer } from '@tanstack/react-virtual';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { MarkdownFilePaths } from '@/shared/components/Markdown';
import { MarkdownLazy } from '@/shared/components/MarkdownLazy';
import { MarkdownEditor } from '@/shared/components/markdownEditor/MarkdownEditor';
import { findWorkspacePaths } from '@/shared/components/markdownFilePaths';
import {
  useWorkspaceFileExistence,
  useWorkspaceFilePreviewLink,
} from '@/features/workspace-files';
import { DRAWER_PROSE_CLASS } from '../prose';
import { displayNameOf } from '@/shared/components/accountIdentity';
import { UserAvatar } from '@/shared/components/UserAvatar';
import { ViaTool } from '@/features/editor-access';
import { absoluteDateTime, formatRelativeTime } from '@/shared/utils/time';
import { useAddTaskComment, useTaskComments } from '../hooks/useTaskComments';
import { serialize, type MentionedPerson } from '../mentions';
import { MentionTextarea } from './MentionTextarea';
import { useSheetBodyScroller } from '../hooks/useSheetBodyScroller';
import { runLabelIndex, type RunLabel } from '../runLabel';
import type { TaskActorType, TaskView } from '@/lib/api/generated/model';
import { PendingRows } from '@/shared/components/PendingRows';
import { ReadFailure } from '@/shared/components/ReadFailure';
import { ACCESS_COPY } from '@/features/workspaces/accessCopy';
import { useCanWork } from '@/features/workspaces/canWork';

const ESTIMATED_ROW_HEIGHT = 88;

/**
 * What a row says when there is no person to name.
 *
 * `actor` is null for exactly two reasons, and they are not the same fact, which
 * is why `actor_type` is still consulted: a `system` row has nobody to name by
 * contract, while a `user` row that resolved to nobody is a person we failed to
 * look up. This used to say `Person` for the second case *and* for every
 * successfully resolved author, which is what made the missing name invisible —
 * the timeline looked finished while naming nobody at all.
 *
 * `Unknown person` rather than `Unknown` alone: `TaskDetailsPanel` says the
 * latter inside a field already labelled "Created by", and this label carries no
 * such heading.
 *
 * `TaskMovesPanel` keeps a map of its own, with its own wording, on purpose —
 * the two surfaces are not meant to be kept in step, so neither map should be
 * changed to match the other.
 */
const ACTOR_LABELS: Record<TaskActorType, string> = {
  user: 'Unknown person',
  system: 'Agent',
};

/**
 * Already-reported execution ids, so a row that re-renders (a virtualised list
 * re-measures constantly) warns once rather than on every pass.
 */
const reportedMissingRuns = new Set<string>();

/**
 * The step an agent comment was written at, or `undefined` when it cannot be
 * resolved.
 *
 * Two different reasons end up at the same plain `Agent` on screen, and only
 * one of them is normal. No index at all means the call site passed no task —
 * the documented fallback (spec 130 FR-003), silent by design. An index that
 * *does* exist and does not contain the comment's `execution_id` means the
 * task's own run list disagrees with the comment it produced, which is a real
 * data-consistency defect; it still degrades to plain `Agent` rather than
 * failing the row, but it leaves a diagnostic trail.
 */
function resolveStep(
  executionId: string,
  runLabels: ReadonlyMap<string, RunLabel> | null,
): string | undefined {
  if (!runLabels) return undefined;
  const label = runLabels.get(executionId);
  if (label) return label.name;
  if (!reportedMissingRuns.has(executionId)) {
    reportedMissingRuns.add(executionId);
    console.warn(
      `Comment names execution ${executionId}, which is not among the task's runs; falling back to plain "Agent".`,
    );
  }
  return undefined;
}

export interface TaskCommentsPanelProps {
  workspaceId: string;
  taskId: string;
  /**
   * The task whose runs name the steps agent comments were written at (spec
   * 130). Optional: a call site that has no task still renders, with plain
   * `Agent` on every agent row — an unresolvable step is the documented
   * fallback, not an error.
   */
  task?: Pick<TaskView, 'executions'>;
  /** The board's current step labels, preferred over the humanised id. */
  stepLabels?: ReadonlyMap<string, string>;
  /** The file already previewed beside this task: a further open is a replace. */
  previewPath?: string;
  /**
   * The people's comments the task's current step has not run with (spec 235),
   * each badged "New since the last run". Derived by the drawer, which knows the
   * step; absent means none is marked.
   */
  newIds?: ReadonlySet<string>;
  /** The next move after commenting, drawn between the timeline and the composer (spec 235). */
  actionBlock?: ReactNode;
}

/**
 * The shared timeline: people and agents write into the same place, so the
 * account of a task is one account rather than two.
 *
 * Writing a comment does not move the task, resume it, or re-run it (spec 062
 * FR-013). It is not ignored, though: every run reads the task's whole timeline as
 * part of its instructions, so a note reaches the agent on the step's next run —
 * which is why the drawer offers that run right here once someone has written
 * since the last one (spec 235).
 *
 * The box is `ui/textarea`, not a hand-styled one. It used to be the latter, and
 * the cost was four differences from the description field one tab away in this
 * same drawer: no auto-grow, no invalid ring, no disabled treatment, and a
 * different radius and focus ring. Two multiline inputs in one surface have to
 * be the same input.
 *
 * The list scrolls with the drawer, not inside a box of its own (spec 096
 * FR-018). It used to own an `overflow-auto` region nested in `SheetBody`'s,
 * with `h-full` against a parent that had no fixed height — so the moment the
 * composer below it grew, the list had no height budget left and vanished into
 * a sliver. One scroller per drawer: the virtualizer drives `SheetBody` the way
 * the executions rail does, and the composer sits below the rows in normal flow.
 */
/**
 * A comment up to about fifteen lines, then "Show more" (spec 181). An agent's
 * report could run a screen and a half, so the thread became one comment at a
 * time. Measured, not guessed: only a body that runs past the limit gets the
 * fade and the control; the row's height change is observed by the list.
 */
function ClampedBody({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [long, setLong] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const body = ref.current;
    if (!body || open) return;
    const check = () => {
      if (body.scrollHeight > body.clientHeight + 1) setLong(true);
    };
    check();
    const observer = new ResizeObserver(check);
    observer.observe(body);
    for (const child of Array.from(body.children)) observer.observe(child);
    return () => {
      observer.disconnect();
    };
  }, [open]);

  return (
    <div className="flex flex-col items-start gap-1">
      <div
        ref={ref}
        className={
          open
            ? 'w-full'
            : 'relative max-h-80 w-full overflow-hidden data-[long]:[mask-image:linear-gradient(to_bottom,black_calc(100%-3rem),transparent)]'
        }
        data-long={long && !open ? true : undefined}
      >
        {children}
      </div>
      {long ? (
        <Button
          variant="link"
          size="sm"
          className="h-auto px-0"
          aria-expanded={open}
          onClick={() => {
            setOpen(!open);
          }}
        >
          {open ? 'Show less' : 'Show more'}
        </Button>
      ) : null}
    </div>
  );
}

export function TaskCommentsPanel({
  workspaceId,
  taskId,
  task,
  stepLabels,
  previewPath,
  newIds,
  actionBlock,
}: TaskCommentsPanelProps) {
  // Where a workspace path in a comment goes is not decided here: one shared
  // hook owns the breakpoint read, the sub-`sm` fallback and push-versus-replace
  // for every entry point in the app (spec 140 FR-050).
  const previewLink = useWorkspaceFilePreviewLink(workspaceId, previewPath);
  const [draft, setDraft] = useState('');
  // Everyone picked from the @ list; on send, the names still in the text
  // become mentions (spec 137).
  const [chosenPeople, setChosenPeople] = useState<MentionedPerson[]>([]);
  const comments = useTaskComments(workspaceId, taskId);
  const canWork = useCanWork();
  const addComment = useAddTaskComment();
  const sendErrorId = useId();
  const hintId = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const items = useMemo(() => comments.data?.items ?? [], [comments.data]);
  const hasItems = items.length > 0;
  // A file path someone wrote is a door to the file (spec 095 FR-018): the
  // paths across the visible comments are checked against the workspace once,
  // one listing per folder, and each body links the ones that exist.
  const mentioned = useMemo(
    () => [
      ...new Set(items.flatMap((entry) => findWorkspacePaths(entry.body))),
    ],
    [items],
  );
  const existence = useWorkspaceFileExistence(workspaceId, mentioned);
  /**
   * The step every run of this task ran at, keyed by execution id — built by
   * the same `runLabelIndex` the Results tab reads, not a second resolver
   * (spec 130). `null` when there is no task, which is how a call site without
   * one degrades to plain `Agent`.
   */
  const runLabels = useMemo(
    () => (task ? runLabelIndex(task, stepLabels) : null),
    [task, stepLabels],
  );
  const filePaths: MarkdownFilePaths = {
    exists: existence.exists,
    // Only the *owner* of this destination moved (FR-051): this panel still
    // decides which paths in which bodies become links, it just no longer
    // carries its own copy of where they lead.
    linkTo: previewLink,
  };
  // Re-measured when the list first appears: it mounts only once comments have
  // loaded, after the hook's first pass found no list at all.
  const { getScrollElement, scrollMargin } = useSheetBodyScroller(
    listRef,
    hasItems,
  );

  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Virtual's returned functions can't be safely memoized by the React Compiler; this is expected.
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    scrollMargin,
    measureElement: (element) => element.getBoundingClientRect().height,
  });

  // Whitespace is not content. Refused here as well as by the service, so the
  // answer arrives as you type rather than after a round trip.
  const canSend = draft.trim().length > 0 && !addComment.isPending;

  const send = () => {
    if (!canSend) return;
    addComment.mutate(
      { workspaceId, taskId, body: serialize(draft.trim(), chosenPeople) },
      // Cleared only once it is stored. Clearing on submit would lose what
      // someone wrote the moment the network did.
      {
        onSuccess: () => {
          setDraft('');
          setChosenPeople([]);
        },
      },
    );
  };

  return (
    <section className="flex flex-col gap-5">
      {comments.isPending ? (
        <PendingRows label="Loading comments" testId="comments-state" />
      ) : null}

      {comments.isError ? (
        // `text-destructive`, not the board's `failed` role. That token is what
        // the board says *this task has failed* in — the card rail, the state
        // dot, the Overview counter — and a sentence about a request that did
        // not come back is not a claim about the task.
        <ReadFailure
          message="The comments could not be loaded."
          onRetry={() => {
            void comments.refetch();
          }}
          isFetching={comments.isFetching}
          testId="comments-state"
        />
      ) : null}

      {comments.data && items.length === 0 ? (
        <p
          className="text-muted-foreground text-sm"
          data-testid="comments-state"
        >
          No comments yet.
        </p>
      ) : null}

      {comments.data && items.length > 0 ? (
        // Three ways a row can say who wrote it, in precedence order. A
        // trigger-caused comment gets a "Trigger: {name}" badge from
        // `caused_by_trigger_profile_name` and *only* that — its actor is the
        // system, so "Agent" beside the trigger's name would add nothing. A
        // `user` row names its author from `actor`. Everything else falls back
        // to `ACTOR_LABELS`.
        //
        // `actor_type` decides the branch, not `actor`: the contract does not
        // tie the two together, so a `system` row that happened to carry a
        // person would otherwise be shown as that person's words.
        //
        // `TaskMovesPanel` still uses the badge the same way; only the author
        // half is unique to here, for the contract reason `ACTOR_LABELS` gives.
        <div
          ref={listRef}
          className="relative w-full"
          style={{ height: virtualizer.getTotalSize() }}
        >
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const entry = items[virtualRow.index];
            /*
              The step qualifier is gated on `actor_type === 'system'` and a
              step that actually resolved — never on "we reached the fallback
              branch below". That branch is also where a `user` comment whose
              author we failed to look up lands, and `Unknown person @ Implement`
              would attribute a person's words to a run.

              `step_kind_snapshot` is deliberately not consulted: `system` alone
              decides a comment is an agent's, so a comment written at a
              non-`ai` step is still `Agent @ <step>`.
            */
            const step =
              entry.actor_type === 'system' && entry.execution_id
                ? (resolveStep(entry.execution_id, runLabels) ?? null)
                : null;
            // Text and machine-readable value from one parse, so `dateTime`
            // cannot disagree with the text beside it. `machine` is absent
            // for an unparsable value — an attribute set to an unparsable
            // string is worse than an absent one.
            const moment = absoluteDateTime(entry.created_at);
            // Composed once so the visible line and its `title` cannot drift
            // apart.
            const attribution = step
              ? `${ACTOR_LABELS[entry.actor_type]} @ ${step}`
              : ACTOR_LABELS[entry.actor_type];
            return (
              <div
                key={entry.id}
                data-testid="comment-row"
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${String(virtualRow.start - scrollMargin)}px)`,
                }}
                className="pb-3"
              >
                <div className="border-border bg-card flex flex-col gap-1.5 rounded-lg border p-3">
                  <div className="text-muted-foreground flex flex-wrap items-center justify-between gap-x-2 gap-y-1 text-xs">
                    {entry.caused_by_trigger_profile_name ? (
                      <Badge variant="secondary" className="shrink-0">
                        Trigger: {entry.caused_by_trigger_profile_name}
                      </Badge>
                    ) : entry.actor_type === 'user' && entry.actor ? (
                      // Keeps its width, so on a phone the header wraps the time
                      // and the "new" badge below the name instead of cutting
                      // the name, and the tool, to nothing (spec 246).
                      <span className="flex max-w-full min-w-0 shrink-0 flex-wrap items-center gap-x-1.5">
                        {/* Decorative: the name is right beside it, and a disc
                              announced as well as read is the person said twice. */}
                        <UserAvatar
                          id={entry.actor.id}
                          displayName={entry.actor.display_name}
                          email={entry.actor.email}
                        />
                        {/*
                            The address on hover, not on the line. Two colleagues
                            sharing a display name are exactly what a name alone
                            cannot tell apart, and this row has a relative
                            timestamp to fit as well — the same trade
                            `TaskDetailsPanel`'s "Created by" makes.
                          */}
                        <span
                          className="text-foreground truncate font-medium"
                          title={entry.actor.email}
                        >
                          {displayNameOf(
                            entry.actor.display_name,
                            entry.actor.email,
                          )}
                        </span>
                        {/* Written through their AI tool (spec 246). */}
                        <ViaTool via={entry.via} />
                      </span>
                    ) : (
                      <span className="flex min-w-0 items-center gap-1.5">
                        {/* A disc for the agent, as a person's comment has
                            theirs (spec 181). Decorative: the words beside it
                            say who. */}
                        <span
                          aria-hidden="true"
                          className="bg-muted text-muted-foreground flex size-5 shrink-0 items-center justify-center rounded-full"
                        >
                          <BotIcon className="size-3" strokeWidth={1.75} />
                        </span>
                        <span
                          className="text-foreground min-w-0 truncate font-medium"
                          /* The untruncated attribution, only where a step
                               resolved — a `title` repeating the one visible word
                               `Agent` is noise. */
                          title={step ? attribution : undefined}
                        >
                          {attribution}
                        </span>
                      </span>
                    )}
                    {/*
                        The moment itself, on the line (spec 130 FR-010). It used
                        to read "2h ago" with the exact time on hover, which is
                        right for a file listing and wrong for a record: a comment
                        is read weeks later, and "2h ago" cannot be read at all
                        then. The machine-readable value stays exact in
                        `dateTime` — omitted rather than set to an unparsable
                        value, since an invalid `datetime` is worse than none.

                        The relative phrasing keeps its place on the `title`
                        (FR-012), where it earns its keep for a recent comment:
                        "2h ago" is the read the absolute line cannot give. Past
                        `formatRelativeTime`'s 30-day cutoff it degrades to a
                        locale date, so for the weeks-later reader the hover
                        adds little — accepted rather than branched on, and the
                        reason it is only a hover.

                        Computed per render, with no interval: the visible line
                        is absolute and does not go stale, and re-rendering
                        every virtualised row to freshen a tooltip nobody is
                        pointing at costs more than it is worth.

                        The `title` is gated on a parsed moment for the same
                        reason the attribution's is: an unparsable `created_at`
                        would otherwise put `—` in both the text and the
                        tooltip.
                      */}
                    <time
                      className="shrink-0 tabular-nums"
                      dateTime={moment.machine}
                      title={
                        moment.machine
                          ? formatRelativeTime(entry.created_at)
                          : undefined
                      }
                    >
                      {moment.text}
                    </time>
                    {newIds?.has(entry.id) ? (
                      <Badge variant="secondary" className="shrink-0">
                        New since the last run
                      </Badge>
                    ) : null}
                  </div>
                  {/* As written, not as typed: people and agents both write
                        markdown here, and a `**` shown raw is the author's
                        emphasis turned into noise (spec 096 FR-001). */}
                  <ClampedBody>
                    <MarkdownLazy
                      content={entry.body}
                      className={DRAWER_PROSE_CLASS}
                      filePaths={filePaths}
                    />
                  </ClampedBody>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {/* Read, not written, by someone who is not a member (spec 189). */}
      {canWork ? actionBlock : null}

      {canWork ? (
        <div className="flex flex-col gap-2">
          {/*
          The shared markdown editor (spec 145), with the mention box as its
          text box so `@` still offers people. Preview shows the comment as it
          will be posted: the names chosen from the picker as mentions, which
          is what `send` makes of them.
        */}
          <MarkdownEditor
            value={draft}
            onValueChange={setDraft}
            preview={serialize(draft.trim(), chosenPeople)}
            previewClassName={DRAWER_PROSE_CLASS}
            filePaths={filePaths}
            renderInput={(input) => (
              <MentionTextarea
                workspaceId={workspaceId}
                {...input}
                onMention={(person) => {
                  setChosenPeople((chosen) => [...chosen, person]);
                }}
              />
            )}
            aria-label="Add a comment"
            placeholder="Leave a note on this task"
            rows={3}
            aria-invalid={addComment.isError}
            aria-describedby={
              addComment.isError ? `${hintId} ${sendErrorId}` : hintId
            }
            /*
            Locked while the write is in flight, as the description field is.
            Only the button was disabled before, so anything typed in that
            window was silently destroyed by the success handler clearing the
            box — the exact loss the deferred clear exists to prevent, arriving
            by another route.
          */
            disabled={addComment.isPending}
          />
          <p
            id={hintId}
            data-composer-hint=""
            className="text-muted-foreground text-xs"
          >
            Type @ to mention someone.
          </p>

          {addComment.isError ? (
            // Announced. Nothing else on screen changes when a comment fails to
            // send, which is exactly the case a live region is for — and the
            // title, description, label and dialog errors all already say so.
            <p
              id={sendErrorId}
              role="alert"
              className="text-destructive text-sm"
            >
              The comment could not be added.
            </p>
          ) : null}

          <Button
            type="button"
            size="sm"
            // Full width on a phone (spec 270).
            className="self-end max-md:w-full"
            disabled={!canSend}
            onClick={send}
          >
            {addComment.isPending ? 'Sending…' : 'Comment'}
          </Button>
        </div>
      ) : (
        <p className="text-muted-foreground text-sm">
          {ACCESS_COPY.membersOnlyComment}
        </p>
      )}
    </section>
  );
}
